import React, {useState} from "react";
import { TextField } from "formik-material-ui";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import { Formik, Form, Field, FormikProps } from "formik";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import API from "../../../../helpers/apiHelpers";
import { useSnackbar } from "notistack";
import {useDispatch, useSelector} from "react-redux";
import {signUpUser} from "../../../../redux/user/user.actions";
import {RootState, useAppDispatch} from "../../../../redux/store";
import {Checkbox, FormControlLabel, Grid, makeStyles, TextField as RTextField, Typography, useMediaQuery} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";
import {ReactComponent as CheckboxBorder} from "../../../../Images/checkboxBorder.svg";
import {privacyPolicyURL, termsOfUseURL} from "../../../../Constants/api";
import {states} from "../../../../Constants/locations";
import ReactPixel from "react-snapchat-pixel";
import {useTheme} from "@material-ui/core/styles";
import {partners} from "../../../../Constants/partners";

interface StateOption {
    label: string;
    value: string;
}

interface Props {
	setIsOnBoarding: React.Dispatch<React.SetStateAction<boolean>>,
    setIsEmailConfirmed: (boolean) => void,
}

export default ({setIsOnBoarding, setIsEmailConfirmed}: Props) => {
    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .required("Required"),
        emailAddress: Yup.string()
            .email("Invalid email")
            .required("Required"),
        workingStates: Yup.array()
            .min(1, "Required")
            .required("Required"),
        practiceCode: Yup.string()
            .required("Required"),
        password: Yup.string()
            .min(8, "Too Short!")
            .required("Required"),
        passwordConfirm: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords must match")
            .required("Required"),
    });
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const primaryColor = useSelector((state: RootState) => state?.branding.brandHeader);
    const partnerId = useSelector((state: RootState) => state?.branding.partnerId);
    const [checkboxChecked, setCheckboxChecked] = useState(false);

    return (
        <Formik
            initialValues={{
                firstName: "",
                lastName: "",
                emailAddress: "",
                workingStates: [] as StateOption[],
                password: "",
                passwordConfirm: "",
                practiceCode: "",
            }}
            onSubmit={ async (values, {setStatus}) => {
                try{
                    const user = await dispatch(signUpUser({
                        email: values.emailAddress.toLowerCase(),
                        password: values.password,
                        role: "VET",
                        device: isMobile ? "MOBILE" : "WEB"
                    }));
                    API.isLoggedIn = true;
                    API.token = user.authenticationToken.token;
                    API.role = user.authenticationToken.role;
                    API.userId = user.authenticationToken.userId;
                    const userIdentification = { user_email: user.email };
                    ReactPixel.init(process.env.REACT_APP_SNAP_ID, userIdentification);
                    ReactPixel.pageView();
                    localStorage.setItem("profile", JSON.stringify({
                        firstName: values.firstName,
                        lastName: values.lastName,
                        practiceCode: values.practiceCode,
                        workingStates: values.workingStates.map(state => state.value),
                    }));
                    let expiry = new Date();
                    expiry.setTime(expiry.getTime()+604800000);
                    document.cookie = "isOnBoarding=true;path=/;expires="+expiry.toUTCString();
                    setIsOnBoarding(true);
                    setIsEmailConfirmed(false);
                    setStatus({success: true});
                } catch (error) {
                    enqueueSnackbar((error.message || error.errorMessage), {
                        variant: "error",
                    });
                    setStatus({success: false});
                }
            }}
            validationSchema={validationSchema}
        >
            {
                (props: FormikProps<{
					firstName: string,
					lastName: string,
					emailAddress: string,
					workingStates: StateOption[],
					password: string,
					passwordConfirm: string,
					practiceCode: string
				}>) => (
                    <Form>
                        <Box
                            display="flex"
                            flexDirection="column"
                            flexWrap="wrap"
                            width="100%"
                            marginBottom="16px"
                        >
                            <Field
                                component={TextField}
                                name="firstName"
                                id="firstName"
                                aria-labelledby="First Name"
                                label="First Name"
                                variant="outlined"
                                color="secondary"
                                type="text"
                                margin="normal"
                                onChange={props.handleChange}
                                value={props.values.firstName}
                            />
                            <Field
                                component={TextField}
                                name="lastName"
                                id="lastName"
                                aria-labelledby="Last Name"
                                label="Last Name"
                                variant="outlined"
                                color="secondary"
                                type="text"
                                margin="normal"
                                onChange={props.handleChange}
                                value={props.values.lastName}
                            />
                            <Field
                                component={TextField}
                                name="emailAddress"
                                id="emailAddress"
                                aria-labelledby="Email Address"
                                label="Email Address"
                                variant="outlined"
                                color="secondary"
                                type="email"
                                margin="normal"
                                onChange={props.handleChange}
                                value={props.values.emailAddress}
                            />
                            <Autocomplete<StateOption, true, false, false>
                                multiple
                                id="workingStates"
                                options={states}
                                getOptionLabel={(option) => option.label}
                                getOptionSelected={(option, value) => option.value === value.value}
                                value={props.values.workingStates}
                                onChange={(event, value) => {
                                    props.setFieldValue("workingStates", value);
                                }}
                                onBlur={() => props.setFieldTouched("workingStates", true)}
                                renderInput={(params) => (
                                    <RTextField
                                        {...params}
                                        name="workingStates"
                                        label="Licensed state(s)"
                                        variant="outlined"
                                        color="secondary"
                                        margin="normal"
                                        error={Boolean(props.touched.workingStates && props.errors.workingStates)}
                                        helperText={props.touched.workingStates && props.errors.workingStates ? props.errors.workingStates : ""}
                                    />
                                )}
                            />
                            <Field
                                component={TextField}
                                name="practiceCode"
                                id="practiceCode"
                                aria-labelledby="Practice Code"
                                label="Practice Code"
                                variant="outlined"
                                color="secondary"
                                type="text"
                                margin="normal"
                                onChange={props.handleChange}
                                value={props.values.practiceCode}
                            />
                            <Field
                                component={TextField}
                                label="Password"
                                id="password"
                                aria-labelledby="Password"
                                name="password"
                                variant="outlined"
                                color="secondary"
                                type="password"
                                margin="normal"
                                onChange={props.handleChange}
                                value={props.values.password}
                            />
                            <Field
                                component={TextField}
                                label="Confirm Password"
                                id="passwordConfirm"
                                aria-labelledby="Confirm Password"
                                name="passwordConfirm"
                                variant="outlined"
                                color="secondary"
                                type="password"
                                margin="normal"
                                onChange={props.handleChange}
                                value={props.values.passwordConfirm}
                            />
                        </Box>
                        <Box display="flex" justifyContent="flex-start" alignItems="center" marginBottom="15px">
                            <Checkbox
                                color="secondary"
                                icon={<CheckboxBorder />}
                                style={{paddingLeft: 0}}
                                value={checkboxChecked}
                                onChange={() => {setCheckboxChecked(!checkboxChecked);}}
                            />
                            <Box>
                                <Typography color="textPrimary" style={{fontSize: 14}}>
									I agree to the&nbsp;
                                    <a aria-label="read tearms of service" href={termsOfUseURL} target="_blank" rel="noreferrer" style={{color: primaryColor}}>Terms of Service & Privacy Policy</a>
                                </Typography>
                            </Box>
                        </Box>
                        <Box onClick={() => {
                            if(!checkboxChecked) {
                                enqueueSnackbar("Please agree to our Terms Of Service and Privacy Policy first.",
                                    {
                                        variant: "error",
                                        autoHideDuration: 4000
                                    });
                            }
                        }}>
                            <Button
                                color="primary"
                                variant="contained"
                                type="submit"
                                data-testid="submitButton"
                                fullWidth
                                disabled={!checkboxChecked}
                            >
								Continue
                            </Button>
                        </Box>
                    </Form>
                )
            }
        </Formik>
    );
};
